import { useState, useRef, useEffect } from "react";
import { debounce } from "lodash";
import Text from "../shared-components/Text";
import { ADDRESS_ENDPOINT, ADDRESS_RETRIEVE, formPages } from "../../constants";
import FormActionButton from "../shared-components/FormActionButton";
import ButtonComponent from "../shared-components/Button";
import Label from "../shared-components/Label";
import "./AddressDetails.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
export default function AddressDetails({ compensate, onNext, onBack }) {
  // Modal state
  const [showModal, setShowModal] = useState(false);

  // Current address states
  const [addressData, setAddressData] = useState(
    compensate.currentAddressData ?? null,
  );
  const [addressSelected, setAddressSelected] = useState(
    compensate.currentAddressSelected ?? "",
  );
  const [address, setAddress] = useState(compensate.currentAddress ?? "");
  const [enterAddress1, setEnterAddress1] = useState(
    compensate.currentPostcode ?? "",
  );
  const [isSearching, setIsSearching] = useState(false);

  // Dynamic previous address states
  const [previousAddresses, setPreviousAddresses] = useState(
    compensate.previousAddress ?? [],
  );
  const [previousAddressInputs, setPreviousAddressInputs] = useState(() => {
    const inputs = [];
    if (compensate.previousAddress?.length > 0) {
      inputs.push({
        postcode: compensate.previousAddressPostCode ?? "",
        addressData: compensate.previousAddressData ?? null,
        addressSelected: compensate.previousAddressSelected ?? "",
        errors: { postcode: false, selection: false, duplicate: false },
        isSearching: false,
      });
    }
    if (compensate.previousAddress?.length > 1) {
      inputs.push({
        postcode: compensate.previousPreviousAddressPostCode ?? "",
        addressData: compensate.previousPreviousAddressData ?? null,
        addressSelected: compensate.previousPreviousAddressSelected ?? "",
        errors: { postcode: false, selection: false, duplicate: false },
        isSearching: false,
      });
    }
    return inputs;
  });

  // Error states
  const [enterAddress1Error, setEnterAddress1Error] = useState(false);
  const [addressSelectedError, setAddressSelectedError] = useState(false);

  // Helper functions for dynamic labels
  const getPreviousAddressLabel = (index, totalCount) => {
    if (index === 0) return "Previous address";
    return "Additional previous address";
  };

  // Debounced search for current address
  const debouncedAddressSearch = useRef(
    debounce(async (query) => {
      if (!query || query.trim().length < 3) {
        setAddressData(null);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await ADDRESS_ENDPOINT(compensate, query);
        setAddressData(data);
        setAddressSelected("");
        setAddress("");
      } catch (error) {
        console.error("Address search error:", error);
        setAddressData(null);
      }
      setIsSearching(false);
    }, 100),
  ).current;

  useEffect(() => {
    return () => debouncedAddressSearch.cancel();
  }, []);

  useEffect(() => {
    const hasPostcodeError = previousAddressInputs.some(
      (item) => item.errors.postcode,
    );

    if (hasPostcodeError) {
      setShowModal(true);
    }
  }, [previousAddressInputs]);

  // Debounced search for previous addresses
  const previousSearchRefs = useRef({});

  const getDebouncedPreviousSearch = (index) => {
    if (!previousSearchRefs.current[index]) {
      previousSearchRefs.current[index] = debounce(async (query) => {
        if (!query || query.trim().length < 3) {
          setPreviousAddressInputs((prev) =>
            prev.map((item, i) =>
              i === index
                ? { ...item, addressData: null, isSearching: false }
                : item,
            ),
          );
          return;
        }
        setPreviousAddressInputs((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, isSearching: true } : item,
          ),
        );
        try {
          const data = await ADDRESS_ENDPOINT(compensate, query);
          setPreviousAddressInputs((prev) =>
            prev.map((item, i) =>
              i === index
                ? { ...item, addressData: data, isSearching: false }
                : item,
            ),
          );
        } catch (error) {
          console.error("Previous address search error:", error);
          setPreviousAddressInputs((prev) =>
            prev.map((item, i) =>
              i === index
                ? { ...item, addressData: null, isSearching: false }
                : item,
            ),
          );
        }
      }, 100);
    }
    return previousSearchRefs.current[index];
  };

  // Handle clicking on a suggestion for current address
  const handleSuggestionClick = async (item) => {
    if (item.Type === "Address") {
      // Final address - retrieve full details
      const data = await ADDRESS_RETRIEVE(item.Id);
      setAddress(data);
      setAddressSelected(item);
      setEnterAddress1(item.Text);
      setAddressData(null); // Hide suggestions
    } else {
      // Container - drill down for more results
      setIsSearching(true);
      const data = await ADDRESS_ENDPOINT(compensate, item.Text, item.Id);
      setAddressData(data);
      setEnterAddress1(item.Text);
      setIsSearching(false);
    }
  };

  // Handle clicking on a suggestion for previous address
  const handlePreviousSuggestionClick = async (item, index) => {
    if (item.Type === "Address") {
      // Final address - retrieve full details
      const data = await ADDRESS_RETRIEVE(item.Id);
      setPreviousAddresses((prevAddresses) => {
        const newAddresses = [...prevAddresses];
        newAddresses[index] = data;
        return newAddresses;
      });
      setPreviousAddressInputs((prev) =>
        prev.map((input, i) =>
          i === index
            ? {
                ...input,
                addressSelected: item,
                postcode: item.Text,
                addressData: null,
              }
            : input,
        ),
      );
    } else {
      // Container - drill down for more results
      setPreviousAddressInputs((prev) =>
        prev.map((input, i) =>
          i === index ? { ...input, isSearching: true } : input,
        ),
      );
      const data = await ADDRESS_ENDPOINT(compensate, item.Text, item.Id);
      setPreviousAddressInputs((prev) =>
        prev.map((input, i) =>
          i === index
            ? {
                ...input,
                addressData: data,
                postcode: item.Text,
                isSearching: false,
              }
            : input,
        ),
      );
    }
  };

  const addPreviousAddress = () => {
    if (previousAddressInputs.length < 2) {
      setPreviousAddressInputs((prev) => [
        ...prev,
        {
          postcode: "",
          addressData: null,
          addressSelected: "",
          errors: { postcode: false, selection: false, duplicate: false },
          isSearching: false,
        },
      ]);
    }
  };

  const removePreviousAddress = (indexToRemove) => {
    setPreviousAddressInputs((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setPreviousAddresses((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const updatePreviousAddressInput = (index, field, value) => {
    setPreviousAddressInputs((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              errors: { ...item.errors, postcode: false },
            }
          : item,
      ),
    );
  };

  const clearPreviousAddressErrors = (index) => {
    setPreviousAddressInputs((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              errors: { postcode: false, selection: false, duplicate: false },
            }
          : item,
      ),
    );
  };

  return (
    <>
      {/* Modal for missing previous address */}
      {showModal && (
        <div
          className="address-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="address-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="address-modal-header">
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: "var(--ycm-orange)",
                  flexShrink: 0,
                  marginRight: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{ color: "#fff", fontSize: "20px" }}
                />
              </span>
              <span className="address-modal-title">
                It looks like you haven't added a previous address.
              </span>
            </div>

            <p className="address-modal-description">
              Previous addresses are used to locate all your finance agreements.
            </p>

            <div className="address-modal-actions">
              <button
                className="modal-btn-primary"
                onClick={() => setShowModal(false)}
              >
                Add previous address
              </button>
              <button
                className="modal-btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  onNext(
                    {
                      currentAddress: address,
                      previousAddress: previousAddresses,
                    },
                    formPages.Signature,
                  );
                }}
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="d-flex align-items-center heading-icon row fdc-form"
        data-testid="page-address"
        style={{ paddingBottom: "0" }}
      >
        <h2 className="title">
          Your address is key in locating your vehicle finance agreements.
        </h2>
        <p className="mb-0" style={{ fontWeight: 600, marginBottom: "8px" }}>
          Please start with your current address and include all previous
          addresses.
        </p>
      </div>
      <div className="fdc-form gif-input" style={{ marginTop: "0" }}>
        <div className="form-block address-autocomplete">
          <Text
            label={"Current address"}
            htmlFor="UK Address Search"
            onChange={(e) => {
              setEnterAddress1(e.target.value);
              setEnterAddress1Error(false);
              setAddressSelectedError(false);
              debouncedAddressSearch(e.target.value);
            }}
            id="UK Address Search"
            value={enterAddress1}
            placeholder={"Start typing your address or postcode"}
            testId="address-current-postcode"
          />
          {addressData?.length > 0 && (
            <div className="address-suggestions">
              {addressData.map((item, idx) => (
                <div
                  key={item.Id || idx}
                  className="suggestion-item"
                  onClick={() => {
                    handleSuggestionClick(item);
                    setAddressSelectedError(false);
                  }}
                >
                  <div className="suggestion-text">{item.Text}</div>
                  {item.Description && (
                    <div className="suggestion-description">
                      {item.Description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {enterAddress1Error && (
            <div className="error-message">
              <p>Please enter your postcode</p>
            </div>
          )}
          {addressSelectedError && (
            <div className="error-message">
              <p>Please select an address</p>
            </div>
          )}
        </div>

        {/* Add Previous Address Button */}
        {previousAddressInputs.length === 0 && address && (
          <div style={{ marginTop: "24px", marginBottom: "24px" }}>
            <ButtonComponent
              className="btn-w next-button"
              label="+ Click to add a previous address"
              onClick={addPreviousAddress}
              style={{ padding: "10px 20px" }}
            />
          </div>
        )}

        {/* Info Box Section - Similar to CreditCheckSuccess */}
        {previousAddressInputs.length === 0 && address && (
          <div className="mb-3">
            <div className="d-flex align-items-start gap-2 form-block mb-1 info-box">
              <img src="/icons/lucid-icons/badge-alert.svg" alt="badge-alert" />
              <label className="form-label">
                Add your previous addresses to enable us to find all your car
                finance agreements.
              </label>
            </div>
          </div>
        )}

        <div>
          {/* Dynamic Previous Address Sections */}
          {previousAddressInputs.map((addressInput, index) => (
            <div key={`previous-address-${index}`}>
              <div>
                <div className="form-block mb-3 d-flex justify-content-between align-items-center">
                  <Label
                    label={getPreviousAddressLabel(
                      index,
                      previousAddressInputs.length,
                    )}
                  />
                  <button
                    className="btn-delete"
                    onClick={() => removePreviousAddress(index)}
                    aria-label="Remove"
                  >
                    <img src="/icons/lucid-icons/trash.svg" alt="" />
                  </button>
                </div>
              </div>
              <div className="form-block address-autocomplete">
                <Text
                  label=""
                  htmlFor={`Previous Address Search ${index}`}
                  onChange={(e) => {
                    updatePreviousAddressInput(
                      index,
                      "postcode",
                      e.target.value,
                    );
                    clearPreviousAddressErrors(index);
                    getDebouncedPreviousSearch(index)(e.target.value);
                  }}
                  id={`Previous Address Search ${index}`}
                  value={addressInput.postcode}
                  placeholder={"Start typing your address or postcode"}
                  testId={`address-previous-postcode-${index}`}
                />
                {addressInput.addressData?.length > 0 && (
                  <div className="address-suggestions">
                    {addressInput.addressData.map((item, idx) => (
                      <div
                        key={item.Id || idx}
                        className="suggestion-item"
                        onClick={() => {
                          handlePreviousSuggestionClick(item, index);
                          clearPreviousAddressErrors(index);
                        }}
                      >
                        <div className="suggestion-text">{item.Text}</div>
                        {item.Description && (
                          <div className="suggestion-description">
                            {item.Description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* {addressInput.errors.postcode && (
                  
                  <div className="error-message">
                    <p>Please enter your postcodeggg</p>
                    setShowModal(true);
                  </div>
                )} */}
                {addressInput.errors.selection && (
                  <div className="error-message">
                    <p>Please select an address</p>
                  </div>
                )}
                {addressInput.errors.duplicate && (
                  <div className="error-message">
                    <p>
                      This address has already been provided or matches your
                      current address
                    </p>
                  </div>
                )}
              </div>

              {/* Add Another Previous Address Button */}
              {index === previousAddressInputs.length - 1 &&
                previousAddressInputs.length < 2 &&
                previousAddresses[index] && (
                  <div style={{ marginTop: "24px", marginBottom: "24px" }}>
                    <ButtonComponent
                      label="+ Click to add another previous address"
                      onClick={addPreviousAddress}
                      className="btn-w next-button"
                      style={{ padding: "10px 20px" }}
                    />
                  </div>
                )}

              {/* Info Box Section - Under Add Another Previous Address Button */}
              {index === previousAddressInputs.length - 1 &&
                previousAddressInputs.length < 2 &&
                previousAddresses[index] && (
                  <div
                    className="mb-3"
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                  >
                    <div className="d-flex align-items-start gap-2 form-block mb-1 info-box">
                      <img
                        src="/icons/lucid-icons/badge-alert.svg"
                        alt="badge-alert"
                      />
                      <label className="form-label">
                        Add your previous addresses to enable us to find all
                        your car finance agreements.
                      </label>
                    </div>
                  </div>
                )}
            </div>
          ))}

          <FormActionButton
            backTestId="address-btn-back"
            nextTestId="address-btn-next"
            onBack={() => {
              onBack(formPages.ContactDetails, {
                // Preserve existing data from previous pages
                title: compensate.title,
                firstName: compensate.firstName,
                surname: compensate.surname,
                dob: compensate.dob,
                previousName: compensate.previousName,
                claims: compensate.claims || [],
                email: compensate.email,
                confirmEmail: compensate.confirmEmail,
                mobile: compensate.mobile,
                // Add current AddressDetails data
                currentAddress: address,
                currentPostcode: enterAddress1,
                currentAddressSelected: addressSelected,
                currentAddressData: addressData,
                previousAddress: previousAddresses,
                previousAddressPostCode:
                  previousAddressInputs[0]?.postcode || "",
                previousAddressData:
                  previousAddressInputs[0]?.addressData || null,
                previousAddressSelected:
                  previousAddressInputs[0]?.addressSelected || "",
                previousPreviousAddressPostCode:
                  previousAddressInputs[1]?.postcode || "",
                previousPreviousAddressData:
                  previousAddressInputs[1]?.addressData || null,
                previousPreviousAddressSelected:
                  previousAddressInputs[1]?.addressSelected || "",
              });
            }}
            onNext={() => {
              let hasErrors = false;

              // Current address validation
              if (!enterAddress1) {
                setEnterAddress1Error(true);
                hasErrors = true;
              }
              if (enterAddress1 && !addressSelected) {
                setAddressSelectedError(true);
                hasErrors = true;
              }

              // If current address is valid but no previous address added, show modal
              if (
                address &&
                enterAddress1 &&
                addressSelected &&
                previousAddressInputs.length === 0
              ) {
                setShowModal(true);
                return;
              }

              // Previous address validation
              previousAddressInputs.forEach((addressInput, index) => {
                if (!addressInput.postcode) {
                  setPreviousAddressInputs((prev) =>
                    prev.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            errors: { ...item.errors, postcode: true },
                          }
                        : item,
                    ),
                  );
                  hasErrors = true;
                }
                if (addressInput.postcode && !addressInput.addressSelected) {
                  setPreviousAddressInputs((prev) =>
                    prev.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            errors: { ...item.errors, selection: true },
                          }
                        : item,
                    ),
                  );
                  hasErrors = true;
                }
              });

              // Check for duplicate addresses
              const allAddressIds = [
                address?.Id,
                ...previousAddresses.map((addr) => addr?.Id),
              ].filter(Boolean);
              const uniqueIds = new Set(allAddressIds);

              if (allAddressIds.length !== uniqueIds.size) {
                // Mark duplicate addresses
                previousAddressInputs.forEach((addressInput, index) => {
                  const currentId = previousAddresses[index]?.Id;
                  if (currentId) {
                    const isDuplicate =
                      address?.Id === currentId ||
                      previousAddresses.some(
                        (addr, i) => i !== index && addr?.Id === currentId,
                      );
                    if (isDuplicate) {
                      setPreviousAddressInputs((prev) =>
                        prev.map((item, i) =>
                          i === index
                            ? {
                                ...item,
                                errors: { ...item.errors, duplicate: true },
                              }
                            : item,
                        ),
                      );
                      hasErrors = true;
                    }
                  }
                });
              }

              // Check if all required fields are valid
              const currentAddressValid =
                address && enterAddress1 && addressSelected;
              const allPreviousAddressesValid = previousAddressInputs.every(
                (input) => input.postcode && input.addressSelected,
              );

              if (
                currentAddressValid &&
                allPreviousAddressesValid &&
                !hasErrors
              ) {
                onNext(
                  {
                    currentAddress: address,
                    // currentPostcode: enterAddress1,
                    // currentAddressSelected: addressSelected,
                    // currentAddressData: addressData,
                    previousAddress: previousAddresses,
                    // previousAddressPostCode: previousAddressInputs[0]?.postcode || '',
                    // previousAddressData: previousAddressInputs[0]?.addressData || null,
                    // previousAddressSelected: previousAddressInputs[0]?.addressSelected || '',
                    // previousPreviousAddressPostCode: previousAddressInputs[1]?.postcode || '',
                    // previousPreviousAddressData: previousAddressInputs[1]?.addressData || null,
                    // previousPreviousAddressSelected: previousAddressInputs[1]?.addressSelected || '',
                  },
                  formPages.Signature,
                );
              }
            }}
          />
        </div>
      </div>
    </>
  );
}
